'use client';

import type { ReactNode, Ref } from 'react';
import { FormActions } from '../../components/FormActions';
import { PageTitle } from '../../components/PageTitle';
import { SignupShell } from '../../components/SignupShell';
import { StepStatusRegion } from '../../components/StepStatusRegion';
import { ensureWrapUpStyle } from '../../wrap-up/wrap-up-style';

export interface WrapUpFrameProps {
  title: string;
  description?: string;
  headingRef: Ref<HTMLHeadingElement>;
  /** The step's content, ending with its `WrapUpActions`. */
  children?: ReactNode;
}

/** The shared frame of a wrap-up step: the title, then the step's content and actions. */
export function WrapUpFrame({ title, description, headingRef, children }: WrapUpFrameProps) {
  ensureWrapUpStyle();
  return (
    <SignupShell>
      <PageTitle title={title} description={description} headingRef={headingRef} />
      <section className="signup-step" aria-label={title} data-testid="signup-step">
        {children}
      </section>
    </SignupShell>
  );
}

export interface WrapUpActionsProps {
  status?: string;
  statusTone?: 'neutral' | 'error';
  children: ReactNode;
}

/**
 * The step's live region and its buttons, in the same action bar every other
 * step uses: sticky at the bottom on a phone, inline under the content on a
 * desktop. It must be the last thing in the step. The buttons are children
 * rather than a prop, so the analytics catalogue scanner, which walks JSX
 * children, finds each control's action id.
 */
export function WrapUpActions({
  status = '',
  statusTone = 'neutral',
  children,
}: WrapUpActionsProps) {
  return (
    <>
      <StepStatusRegion message={status} tone={statusTone} />
      <FormActions>{children}</FormActions>
    </>
  );
}
